import { createTheme } from '@mui/material/styles';
import { colors, shadows, typography as designTypography, gradient } from './designTheme';

// Single source of truth: MUI palette is derived from designTheme tokens.
// "lightTheme" = Lucid Architect. "darkTheme" kept as a stub placeholder.

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary:   { main: colors.accent,  dark: colors.accentHover, contrastText: colors.textOnAccent },
    secondary: { main: colors.secondary, contrastText: '#FFFFFF' },
    error:     { main: colors.error },
    warning:   { main: colors.warning },
    success:   { main: colors.low },
    background: { default: colors.bg, paper: colors.surface },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary, disabled: colors.textMuted },
    divider: colors.border,
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: designTypography.fontFamily,
    // Display / headlines — Manrope, tight tracking (editorial feel)
    h1: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontFamily: designTypography.fontFamilyDisplay, fontWeight: 600, fontSize: '1.125rem' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6, color: colors.textSecondary },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: colors.bg, color: colors.textPrimary },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          borderRadius: 12,
          border: 'none',
          boxShadow: shadows.card,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 18px',
        },
        containedPrimary: {
          background: gradient.primary,
          color: colors.textOnAccent,
          boxShadow: 'none',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': {
            background: gradient.primary,
            boxShadow: shadows.button,
            transform: 'translateY(-1px)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.accent,
          '&:hover': {
            backgroundColor: colors.accentDim,
            borderColor: 'rgba(0, 90, 181, 0.30)',
          },
        },
        text: {
          color: colors.accent,
          '&:hover': { backgroundColor: colors.accentDim },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surfaceInput,
          borderRadius: '0.375rem',
          transition: 'background-color 0.15s, box-shadow 0.15s',
          '& fieldset': { borderColor: 'transparent' },
          '&:hover fieldset': { borderColor: 'transparent' },
          '&.Mui-focused': {
            backgroundColor: colors.surface,
            boxShadow: '0 0 0 2px rgba(0, 90, 181, 0.20)',
          },
          '&.Mui-focused fieldset': { borderColor: 'transparent' },
        },
        input: {
          '&::placeholder': { color: colors.textMuted, opacity: 1 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
          fontSize: '11px',
          letterSpacing: '0.02em',
          border: 'none',
        },
        filled: {
          backgroundColor: colors.secondaryBg,
          color: colors.secondary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bg,
          color: colors.textPrimary,
          borderRight: 'none',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          color: colors.textPrimary,
          borderBottom: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.borderSubtle },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.textPrimary,
          color: '#FFFFFF',
          fontSize: '12px',
          fontFamily: designTypography.fontFamily,
          borderRadius: 6,
        },
        arrow: { color: colors.textPrimary },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: 'none' },
        head: {
          color: colors.textSecondary,
          fontFamily: designTypography.fontFamilyDisplay,
          fontWeight: 700,
          fontSize: '10px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          backgroundColor: colors.surfaceElevated,
        },
      },
    },
  },
});

// Placeholder — the Lucid Architect system is light-only for now.
// ThemeProvider still toggles, so keep an object here rather than breaking imports.
const darkTheme = lightTheme;

export { lightTheme, darkTheme };
